import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "src/components/ui/dialog";
import { ErrorContent, ServerConnectionContent, reportStatusFrom } from "./ErrorScreen";
import {
  getErrorMessage,
  isIgnorableError,
  reportError,
  setErrorReporterTokenGetter,
} from "src/lib/errorReporter";
import {
  isErrorHandled,
  isUnexpectedApiError,
  markErrorHandled,
  subscribeGlobalErrors,
} from "src/lib/globalErrors";
import { isBackendReachable, reconnectDelayMs } from "src/lib/backendStatus";

export { notifyGlobalError } from "src/lib/globalErrors";

// Delay before handling window errors: in development React re-dispatches errors caught by
// error boundaries to window, so give boundaries/RouteError time to mark them as handled.
const WINDOW_ERROR_DELAY_MS = 300;

/*
 * Global listener for non-fatal errors (must be rendered inside ClerkProvider and the router):
 * - errors sent through notifyGlobalError (e.g. API 5xx / network failures from api.js)
 * - window "error" and "unhandledrejection" events
 * Shows a single modal at a time; further errors while it is open only bump a counter.
 * API 5xx / network failures first check whether the backend can be reached at all: if not,
 * the modal shows a "Connecting to the server" loading state that retries automatically
 * (nothing is reported, the backend could not receive it) instead of the error.
 */
export default function GlobalErrorProvider({ children }) {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [modal, setModal] = useState(null);
  const modalRef = useRef(null);
  const nextId = useRef(0);
  const primaryButtonRef = useRef(null);
  const checkingRef = useRef(false); // A reachability check is running

  const updateModal = useCallback((id, changes) => {
    if (modalRef.current?.id !== id) return;
    modalRef.current = { ...modalRef.current, ...changes };
    setModal(modalRef.current);
  }, []);

  useEffect(() => {
    setErrorReporterTokenGetter(() => getToken());
  }, [getToken]);

  const showErrorModal = useCallback((error, meta = {}) => {
    const report = reportError(error, meta);

    if (modalRef.current) {
      if (modalRef.current.mode === "connecting") return; // Most likely a consequence of the outage
      modalRef.current = { ...modalRef.current, extraCount: modalRef.current.extraCount + 1 };
      setModal(modalRef.current);
      return;
    }

    const id = ++nextId.current;
    modalRef.current = {
      id,
      mode: "error",
      error,
      requestId: meta.requestId ?? error?.requestId,
      reportStatus: "sending",
      reportId: undefined,
      extraCount: 0,
    };
    setModal(modalRef.current);

    report.then((result) => {
      updateModal(id, { reportStatus: reportStatusFrom(result), reportId: result.reportId });
    });
  }, [updateModal]);

  const showConnecting = useCallback(() => {
    if (modalRef.current?.mode === "connecting") return;
    modalRef.current = { id: ++nextId.current, mode: "connecting", attempt: 0, restored: false, retryAt: 0 };
    setModal(modalRef.current);
  }, []);

  const showError = useCallback(
    (error, meta = {}) => {
      if (isIgnorableError(error)) return;

      if (!isUnexpectedApiError(error)) {
        showErrorModal(error, meta);
        return;
      }
      // API 5xx / network failure: is the backend reachable at all?
      if (modalRef.current?.mode === "connecting" || checkingRef.current) return;
      checkingRef.current = true;
      isBackendReachable().then((reachable) => {
        checkingRef.current = false;
        if (reachable) showErrorModal(error, meta);
        else showConnecting();
      });
    },
    [showErrorModal, showConnecting]
  );

  // Reconnection loop while the "connecting" modal is open
  const connectingId = modal?.mode === "connecting" && !modal.restored ? modal.id : null;
  const attempt = modal?.attempt ?? 0;
  const retryAt = modal?.retryAt ?? 0;
  useEffect(() => {
    if (connectingId === null) return;
    let cancelled = false;
    const delay = retryAt > 0 ? 0 : reconnectDelayMs(attempt);
    const timer = setTimeout(async () => {
      const reachable = await isBackendReachable();
      if (cancelled) return;
      if (reachable) updateModal(connectingId, { restored: true });
      else updateModal(connectingId, { attempt: attempt + 1, retryAt: 0 });
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [connectingId, attempt, retryAt, updateModal]);

  const retryNow = useCallback(() => {
    if (modalRef.current?.mode === "connecting") updateModal(modalRef.current.id, { retryAt: Date.now() });
  }, [updateModal]);

  // Errors notified programmatically (api.js)
  useEffect(() => subscribeGlobalErrors(({ error, meta }) => showError(error, meta)), [showError]);

  // Uncaught errors and unhandled promise rejections
  useEffect(() => {
    const timers = new Set();

    const onError = (event) => {
      const error = event.error || new Error(event.message || "Unknown error");
      if (isIgnorableError(error, { filename: event.filename })) return;
      const timer = setTimeout(() => {
        timers.delete(timer);
        if (isErrorHandled(error)) return;
        markErrorHandled(error);
        showError(error, {
          source: "window",
          extra: { filename: event.filename, lineno: event.lineno, colno: event.colno },
        });
      }, WINDOW_ERROR_DELAY_MS);
      timers.add(timer);
    };

    const onRejection = (event) => {
      const reason = event.reason;
      if (isErrorHandled(reason)) return; // e.g. ApiError already shown by api.js
      if (reason?.name === "ApiError" && !isUnexpectedApiError(reason)) return; // 4xx / 401
      const error = reason instanceof Error ? reason : new Error(getErrorMessage(reason));
      if (isIgnorableError(error)) return;
      markErrorHandled(reason);
      showError(error, { source: "unhandledrejection" });
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      timers.forEach(clearTimeout);
    };
  }, [showError]);

  const dismiss = useCallback(() => {
    modalRef.current = null;
    setModal(null);
  }, []);

  const goHome = useCallback(() => {
    dismiss();
    navigate("/index");
  }, [dismiss, navigate]);

  return (
    <>
      {children}
      <Dialog open={!!modal} onOpenChange={(open) => !open && dismiss()}>
        {modal && (
          <DialogContent
            role="alertdialog"
            aria-modal="true"
            className="max-h-[90vh] w-[calc(100%-2rem)] overflow-y-auto rounded-lg"
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              primaryButtonRef.current?.focus();
            }}
          >
            {modal.mode === "connecting" ? (
              <ServerConnectionContent
                restored={modal.restored}
                attempt={modal.attempt}
                onRetryNow={retryNow}
                onReload={() => window.location.reload()}
                onDismiss={dismiss}
                primaryButtonRef={primaryButtonRef}
                titleAs={DialogTitle}
                descriptionAs={DialogDescription}
              />
            ) : (
            <ErrorContent
              error={modal.error}
              reportStatus={modal.reportStatus}
              reference={modal.reportId || modal.requestId}
              extraCount={modal.extraCount}
              onReload={() => window.location.reload()}
              onHome={goHome}
              onDismiss={dismiss}
              primaryButtonRef={primaryButtonRef}
              titleAs={DialogTitle}
              descriptionAs={DialogDescription}
            />
            )}
          </DialogContent>
        )}
      </Dialog>
    </>
  );
}
